import React, { useCallback, useEffect } from 'react';
import { Button, Spacer, AppView, CardView, withAnimatedNavigationHeader, } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useFetchOld as useFetch } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { postUserAddress, selectAddress } from 'actions';
import { selectIsLoggedIn } from 'reducers/selectors';
import { useNavigateToNextScreen } from 'login/hooks/useNavigateToNextScreen';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import BottomPanel from 'design-system/BottomPanel';
import { OnboardingFlow } from 'utils/types/navigation';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import AddressDisplay from './components/AddressDisplay';
const fetchOptions = {
    muteError: true,
    muteResult: true,
};
const ConfirmAddressScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const [, , loading, fetch] = useFetch(fetchOptions);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const address = route.params?.address;
    const flow = route.params?.flow;
    const navigateToNextScreen = useNavigateToNextScreen();
    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={
                /* eslint-disable-next-line no-nested-ternary */
                flow === OnboardingFlow.RentReporting
                    ? HELP_ARTICLES_KEY.RENT_REPORTING_ONBOARDING
                    : flow === OnboardingFlow.Pots
                        ? HELP_ARTICLES_KEY.POTS_ONBOARDING
                        : undefined}/>),
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, flow]);
    const handleBack = useCallback(() => {
        navigation.goBack();
    }, []);
    const handleContinue = useCallback(async () => {
        /**
         * This is the case of address insertion on onboarding
         * AddressPostCodeScreen is always first in the route as we reset the route to it
         */
        if (!isLoggedIn) {
            const result = await fetch(postUserAddress({ ...address, isPrimary: true }));
            if (result?.address) {
                navigateToNextScreen();
            }
            return;
        }
        const { routes } = navigation.getState();
        const monevoChoosePreviousIndex = routes.findIndex((route) => route.name === 'MonevoChoosePreviousAddress');
        /**
         * this is the case when user is trying to add secondary address
         * we need to post the address and set it in the reducer. Popping back
         * to the screen that started the sub-flow rather than a fixed count
         * keeps this correct however many screens it took.
         */
        if (monevoChoosePreviousIndex > 1) {
            const result = await fetch(postUserAddress(address));
            if (result?.address) {
                dispatch(selectAddress(result.address));
                navigation.pop(routes.length - 1 - monevoChoosePreviousIndex);
            }
            return;
        }
        const kycAddressScreenIndex = routes.findIndex((route) => route.name === 'MonevoReviewAddress' ||
            route.name === 'AddressInfoWithStatusScreen');
        const monevoFamilyDetailsScreenIndex = routes.findIndex((route) => route.name === 'MonevoFamilyDetails');
        const rentReportingOnboardingSelectAddressScreenIndex = routes.findIndex((route) => route.name === 'RentReportingOnboardingSelectAddress');
        /**
         * this is the case of monevo or rent reporting flow when the user doesnt have any KYC’d address
         * in this case we skip POA upload and mark the address as primary
         */
        if (kycAddressScreenIndex < 1 &&
            (monevoFamilyDetailsScreenIndex > 1 ||
                rentReportingOnboardingSelectAddressScreenIndex > 1)) {
            const result = await fetch(postUserAddress({ ...address, isPrimary: true }));
            if (result?.address) {
                dispatch(selectAddress(result.address));
                const returnToIndex = monevoFamilyDetailsScreenIndex > 1
                    ? monevoFamilyDetailsScreenIndex
                    : rentReportingOnboardingSelectAddressScreenIndex;
                navigation.pop(routes.length - 1 - returnToIndex);
            }
            return;
        }
        navigation.push('ChangeAddressProgressScreen', {
            address,
            flow: 'CHANGE',
        });
    }, [address, isLoggedIn, navigateToNextScreen]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <AnimatedKeyboardAwareScrollView scrollEventThrottle={16} onScroll={scrollHandler} showsVerticalScrollIndicator={false} contentContainerStyle={paddingBottom}>
            <ScreenInfoHeader smallSubTitle title="Is everything correct?" subTitle="Make sure this information is correct, we might need to ask you to verify it later."/>
            <Spacer h={8}/>
            <CardView noMarginTop noMarginBottom>
              <AddressDisplay address={address}/>
            </CardView>
          </AnimatedKeyboardAwareScrollView>
          <BottomPanel>
            <Button title="Go back" brandReversed onPress={handleBack}/>
            <Spacer h={8}/>
            <Button brand title="Continue" loading={loading} onPress={handleContinue}/>
          </BottomPanel>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default ConfirmAddressScreen;
